import type { Auftrag, PdpAktion, PdpDetails, PdpEntscheidungsAngaben } from '@awf/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import { ApiError } from '../api/client.js';
import { useAngemeldeterNutzer } from '../auth/auth-context.js';
import { hatFreigabeSeite } from '../auftragstypen.js';
import { ALLGEMEINER_FEHLERTEXT } from '../api/errors.js';
import { Seitenkopf } from '../ui/seitenkopf.js';
import { EmptyState, ErrorState, LoadingState } from '../ui/states.js';
import { AbcLegende, BefundBericht, BlockKacheln } from './befund-bloecke.js';
import stile from './entscheidung-page.module.css';
import { istFremd, NUR_ANSICHT_TEXT } from './einsicht.js';
import { EntscheidungSpalte } from './entscheidung-spalte.js';
import { KeywordHinweisKasten, OHNE_KEYWORD_SPERRE } from './keyword-hinweis.js';
import { type BlockSchluessel, zahlenAusDetails } from './pdp-bloecke.js';
import { kurzzeile } from './ranking.js';
import type { AuftragsQuelle } from './runs-source.js';
import { useAuftrag } from './use-runs.js';

/** Wohin es nach der Entscheidung weitergeht; laeuft der Auftrag noch, zurueck zum Fortschritt. */
function naechsterSchritt(auftrag: Auftrag): { text: string; pfad: string } {
  const id = encodeURIComponent(auftrag.runId);
  if (hatFreigabeSeite(auftrag)) return { text: 'Jetzt prüfen', pfad: `/auftraege/${id}/freigabe` };
  if (auftrag.status === 'fertig')
    return { text: 'Ergebnis öffnen', pfad: `/auftraege/${id}/ergebnis` };
  return { text: 'Zum Fortschritt', pfad: `/auftraege/${id}` };
}

type DetailZustand =
  | { art: 'laedt' }
  | { art: 'daten'; details: PdpDetails }
  | { art: 'fehler'; text: string; endgueltig: boolean };

/**
 * Die Entscheidungsseite eines PDP-Auftrags (Spec 2026-10-07, Abschnitt 4.3): links der Befund
 * aus Martins Formular, rechts die mitlaufende Spalte. Kopf und Kacheln kommen aus Firestore und
 * stehen sofort; die Details laedt die Seite ueber die Leseroute. Entscheiden haengt NICHT an den
 * Details -- das Tor darf nicht an einer Leseroute scheitern.
 */
export function EntscheidungPage({
  quelle,
  holen,
  entscheide,
}: {
  quelle: AuftragsQuelle;
  holen: (runId: string) => Promise<PdpDetails>;
  entscheide: (
    runId: string,
    aktion: PdpAktion,
    angaben?: PdpEntscheidungsAngaben,
  ) => Promise<void>;
}) {
  const { runId = '' } = useParams();
  const zustand = useAuftrag(quelle, runId);
  const uid = useAngemeldeterNutzer()?.uid ?? null;
  const auftrag = zustand.art === 'daten' ? zustand.auftrag : null;
  const auditId = auftrag?.typ === 'pdp' ? auftrag.befund?.auditId : undefined;
  const [details, setDetails] = useState<DetailZustand>({ art: 'laedt' });
  const [versuch, setVersuch] = useState(0);

  // Haengt an auditId statt am Auftrag: jeder Snapshot liefert ein neues Objekt, die Details
  // sollen aber nur einmal (und auf "Erneut versuchen") geladen werden.
  useEffect(() => {
    if (!auditId) return;
    let aktiv = true;
    setDetails({ art: 'laedt' });
    holen(runId).then(
      (d) => {
        if (aktiv) setDetails({ art: 'daten', details: d });
      },
      (f: unknown) => {
        if (!aktiv) return;
        const endgueltig =
          f instanceof ApiError && (f.code === 'nicht_gefunden' || f.code === 'kein_befund');
        setDetails({
          art: 'fehler',
          text: f instanceof ApiError ? f.text : ALLGEMEINER_FEHLERTEXT,
          endgueltig,
        });
      },
    );
    return () => {
      aktiv = false;
    };
  }, [holen, runId, auditId, versuch]);

  // Nach der Entscheidung zur Fortschrittsseite mit Schrittleiste (Rueckmeldung 2026-10-13):
  // Rechts nur "Ihre Entscheidung" stehen zu lassen sagte nicht, dass jetzt etwas laeuft.
  const navigiere = useNavigate();
  const entscheideHier = useCallback(
    async (aktion: PdpAktion, angaben?: PdpEntscheidungsAngaben) => {
      await entscheide(runId, aktion, angaben);
      navigiere(`/auftraege/${encodeURIComponent(runId)}`, {
        replace: true,
        state: {
          bestaetigung:
            aktion === 'optimieren'
              ? 'Die Optimierung ist gestartet.'
              : 'Sie haben die Optimierung verworfen.',
        },
      });
    },
    [entscheide, runId, navigiere],
  );

  const waehle = useCallback((s: BlockSchluessel) => {
    // Die Bloecke stehen offen untereinander; die Kachel springt nur hin.
    const element = document.getElementById(`block-${s}`);
    if (element && typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ block: 'start' });
    }
  }, []);

  if (zustand.art === 'laedt') return <LoadingState text="Auftrag wird geladen …" />;
  if (zustand.art === 'fehler')
    return <ErrorState text={zustand.text} onWiederholen={zustand.wiederholen} />;
  if (!auftrag) {
    return (
      <EmptyState
        titel="Dieser Auftrag existiert nicht oder gehört zu einem anderen Konto."
        text="Prüfen Sie den Verweis, über den Sie hierher gekommen sind."
      >
        <Link className="eds-button eds-button--primary" to="/">
          Zur Übersicht
        </Link>
      </EmptyState>
    );
  }
  if (auftrag.typ !== 'pdp')
    return <Navigate to={`/auftraege/${encodeURIComponent(auftrag.runId)}`} replace />;
  const befund = auftrag.befund;
  if (!befund?.auditId) {
    return (
      <EmptyState
        titel="Zu diesem Auftrag gibt es nichts zu entscheiden."
        text={befund?.hinweis ?? 'Die Analyse hat für diese Seite keinen Befund geliefert.'}
      >
        <Link
          className="eds-button eds-button--secondary"
          to={`/auftraege/${encodeURIComponent(auftrag.runId)}`}
        >
          Zum Auftrag
        </Link>
      </EmptyState>
    );
  }

  // Unlesbare Empfehlungen zaehlen nicht als "0 Empfehlungen": dann gilt der Stand aus Firestore.
  const zahlen =
    details.art === 'daten' && !details.details.empfehlungenUnlesbar
      ? zahlenAusDetails(details.details)
      : befund.bloecke;
  const kurz = details.art === 'daten' ? kurzzeile(details.details.ranking) : undefined;
  // Gesamtansicht der Rolle `admin`: Befund lesen ja, entscheiden nein (das Backend weist es ab).
  const fremd = istFremd(auftrag, uid);
  // Wie die Ratgeber-Analyse-Seite: ohne Fokus-Keyword nur Verwerfen (das Backend sperrt mit 409).
  const ohneKeyword = befund.keywordHinweis === 'fehlt';
  const offen = !fremd && auftrag.status === 'wartet_auf_entscheidung';

  return (
    <div className={stile.seite}>
      <Seitenkopf
        titel={befund.h1 || befund.url}
        pfad={[
          { text: 'Übersicht', ziel: '/' },
          {
            text: fremd ? 'Fortschritt des Auftrags' : 'Fortschritt Ihres Auftrags',
            ziel: `/auftraege/${encodeURIComponent(auftrag.runId)}`,
          },
        ]}
        beschreibung={
          <>
            <a className={stile.adresse} href={befund.url} target="_blank" rel="noreferrer">
              {befund.url}
            </a>
            <span className={stile.grund}>
              {fremd
                ? NUR_ANSICHT_TEXT
                : ohneKeyword
                  ? 'Für diese Seite gibt es keine Empfehlungen. Sie können sie rechts verwerfen.'
                  : 'Prüfen Sie die Empfehlungen und entscheiden Sie rechts, ob die Seite optimiert wird.'}
            </span>
          </>
        }
      />
      <div className={stile.arbeitsflaeche}>
        <div className={stile.inhalt}>
          {/* Wie der hervorgehobene Kasten im n8n-Formular, direkt unter Titel und Adresse. */}
          <div className={stile.grundKasten}>
            <p className={stile.grundTitel}>Optimierungsgrund</p>
            <p>
              {befund.grund || 'Optimierung empfohlen'}
              {kurz && <span className={stile.leise}> · {kurz}</span>}
            </p>
          </div>
          <KeywordHinweisKasten seite={befund} entscheidbar={offen} />
          {/* Ohne Fokus-Keyword gaebe es nur Nullen: der Kasten darueber sagt, warum. */}
          {!ohneKeyword && (
            <>
              <BlockKacheln zahlen={zahlen} onWaehle={waehle} />
              <AbcLegende />
            </>
          )}
          {details.art === 'laedt' && <LoadingState text="Analysedaten werden geladen …" />}
          {details.art === 'fehler' &&
            (details.endgueltig ? (
              <div className="eds-alert eds-alert--warning" role="status">
                <p>{details.text}</p>
              </div>
            ) : (
              <ErrorState text={details.text} onWiederholen={() => setVersuch((v) => v + 1)} />
            ))}
          {details.art === 'daten' && <BefundBericht details={details.details} />}
        </div>
        {/* key: Eingaben und Sperre gehoeren zu genau einem Auftrag. */}
        <EntscheidungSpalte
          key={auftrag.runId}
          offen={offen}
          entscheidung={auftrag.pdpEntscheidung}
          seitenart="Produktdetailseite"
          nurVerwerfen={ohneKeyword ? OHNE_KEYWORD_SPERRE : undefined}
          entscheide={entscheideHier}
          geschlossenGrund={fremd ? NUR_ANSICHT_TEXT : undefined}
          weiter={naechsterSchritt(auftrag)}
        />
      </div>
    </div>
  );
}
